import { useEffect, useRef, useState } from 'react';
import './TwoFactorOtpStep.css';

function maskEmail(email = '') {
  const [local, domain] = String(email).split('@');
  if (!local || !domain) return email;
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${'•'.repeat(Math.max(local.length - 2, 3))}@${domain}`;
}

/**
 * Password-login 2FA OTP UI — 6 digit boxes, resend cooldown, masked email.
 */
function TwoFactorOtpStep({
  email,
  otp,
  onOtpChange,
  onResend,
  onBack,
  loading = false,
  submitLabel = 'Verify & Login',
}) {
  const [digits, setDigits] = useState(() => {
    const value = (otp || '').replace(/\D/g, '').slice(0, 6);
    return Array.from({ length: 6 }, (_, i) => value[i] || '');
  });
  const [cooldown, setCooldown] = useState(30);
  const [resending, setResending] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    const value = (otp || '').replace(/\D/g, '').slice(0, 6);
    setDigits(Array.from({ length: 6 }, (_, i) => value[i] || ''));
  }, [otp]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    const firstEmpty = digits.findIndex((d) => !d);
    const idx = firstEmpty === -1 ? 5 : firstEmpty;
    inputsRef.current[idx]?.focus();
  }, []);

  const syncOtp = (nextDigits) => {
    setDigits(nextDigits);
    onOtpChange(nextDigits.join(''));
  };

  const handleDigitChange = (index, raw) => {
    const cleaned = raw.replace(/\D/g, '');
    if (!cleaned) {
      const next = [...digits];
      next[index] = '';
      syncOtp(next);
      return;
    }

    // Paste or multi-digit into one box
    if (cleaned.length > 1) {
      const chars = cleaned.slice(0, 6).split('');
      const next = Array.from({ length: 6 }, (_, i) => chars[i] || '');
      syncOtp(next);
      const focusAt = Math.min(chars.length, 5);
      inputsRef.current[focusAt]?.focus();
      return;
    }

    const next = [...digits];
    next[index] = cleaned;
    syncOtp(next);
    if (index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits];
        next[index] = '';
        syncOtp(next);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        const next = [...digits];
        next[index - 1] = '';
        syncOtp(next);
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = Array.from({ length: 6 }, (_, i) => pasted[i] || '');
    syncOtp(next);
    inputsRef.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      await onResend();
      setCooldown(30);
      syncOtp(Array(6).fill(''));
      inputsRef.current[0]?.focus();
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="tfa-step text-center">
      <div className="tfa-icon-wrap mx-auto mb-2">
        <i className="bi bi-shield-lock-fill" />
      </div>

      <h5 className="fw-bold mb-1">Verify it&apos;s you</h5>
      <p className="text-muted small mb-1">
        We sent a 6-digit code to
      </p>
      <p className="tfa-email mb-3">{maskEmail(email)}</p>

      <div className="tfa-digits mb-2" onPaste={handlePaste}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={6}
            className={`tfa-digit ${digit ? 'is-filled' : ''}`}
            value={digit}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={(e) => e.target.select()}
            aria-label={`Digit ${index + 1}`}
            disabled={loading}
          />
        ))}
      </div>

      <p className="text-muted small mb-3">Code expires in 5 minutes</p>

      <button
        type="submit"
        className="btn btn-primary w-100 py-2 fw-bold rounded-3 shadow-sm mb-2 d-flex align-items-center justify-content-center"
        disabled={loading || digits.join('').length !== 6}
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm me-2" />
        ) : (
          <i className="bi bi-check2-circle me-2" />
        )}
        {submitLabel}
      </button>

      <div className="d-flex flex-column align-items-center gap-1">
        <button
          type="button"
          className="btn btn-link btn-sm text-decoration-none p-0"
          onClick={handleResend}
          disabled={cooldown > 0 || resending || loading}
        >
          {resending
            ? 'Sending…'
            : cooldown > 0
              ? `Resend code in ${cooldown}s`
              : 'Resend code'}
        </button>
        <button
          type="button"
          className="btn btn-link btn-sm text-muted text-decoration-none p-0"
          onClick={onBack}
          disabled={loading}
        >
          <i className="bi bi-arrow-left me-1" />
          Back to password
        </button>
      </div>
    </div>
  );
}

export default TwoFactorOtpStep;
